import _ from "lodash";

export default class BaseRoute {
  static isRouteClass = true;

  // ====== Default knobs (you can override in each route) ======
  /** Revalidate if any query param changed (true by default) */
  revalidateOnQueryAnyChange = true;

  /** Query keys that, if changed, force revalidation (in addition to the above) */
  revalidateOnQueryKeys = []; // e.g. ["status", "page"]

  /** Query keys to ignore when comparing (e.g. cache-buster) */
  ignoreQueryKeys = []; // e.g. ["rv"] in children

  /** Watched params that, if changed, force revalidation */
  revalidateOnParams = []; // e.g. ["id"]

  /** Allow manual revalidation (useRevalidator().revalidate()) when URL is unchanged */
  allowManualRevalidate = true;

  /** Debug logs (enable per route if you need to see why it revalidates) */
  debugRevalidate = false;

  static getInstance(obj) {
    const instance = new obj();
    Object.assign(instance, obj.__services);
    return instance;
  }

  shouldRevalidate({ currentUrl, nextUrl, currentParams, nextParams, defaultShouldRevalidate }) {
    const cur = currentUrl instanceof URL ? currentUrl : new URL(currentUrl);
    const nxt = nextUrl instanceof URL ? nextUrl : new URL(nextUrl);

    const log = (why, extra = {}) => {
      if (this.debugRevalidate) {
        console.info(`[shouldRevalidate:${this.constructor.name}] ${why}`, extra);
      }
    };

    // 1) Changes in watched route params
    for (const k of this.revalidateOnParams || []) {
      if ((currentParams?.[k] ?? null) !== (nextParams?.[k] ?? null)) {
        log("watched route param changed", {
          k,
          from: currentParams?.[k],
          to: nextParams?.[k],
        });
        return true;
      }
    }

    // 2) Changes in explicit query keys
    for (const k of this.revalidateOnQueryKeys || []) {
      const a = cur.searchParams.get(k);
      const b = nxt.searchParams.get(k);
      if (a !== b) {
        log("watched query key changed", { k, from: a, to: b });
        return true;
      }
    }

    // 3) Any change in query (after ignoring certain keys)
    if (this.revalidateOnQueryAnyChange) {
      const toObj = (u) => {
        const o = Object.fromEntries(u.searchParams);
        for (const key of this.ignoreQueryKeys || []) {
          delete o[key];
        }
        return o;
      };
      const A = toObj(cur);
      const B = toObj(nxt);
      if (!_.isEqual(A, B)) {
        log("any query changed (after ignore keys)", {
          from: A,
          to: B,
          ignore: this.ignoreQueryKeys,
        });
        return true;
      }
    }

    // 4) Manual revalidate support: when URL is unchanged
    //    React Router's revalidate() typically keeps the same URL.
    //    Allow it here so existing components calling revalidate() work.
    if (this.allowManualRevalidate && cur.href === nxt.href) {
      log("manual revalidate (URL unchanged) → allow");
      return true;
    }

    // 5) Fall back to the router's default behavior (do not override)
    const fallback =
      typeof defaultShouldRevalidate === "function" ? defaultShouldRevalidate() : false;
    if (fallback) {
      log("defaultShouldRevalidate() === true");
    }
    return fallback;
  }
}
